/**
 * Venue plans API — contract 12.5 `GET /api/venue-plans` (docs/05-dev-spec §4, ADDITIVE
 * v1.6, task T.V1).
 *
 *   12.5 GET /api/venue-plans → `{ data: [VenuePlanDto], meta }`
 *   (#8  PATCH /api/venues/:id with `planOffline`/`planOnline` is the WRITE — the hook
 *        lives in `venues.ts`, because that is where every other #8 write lives.)
 *
 * The shapes mirror the REAL api module (`apps/api/src/venue-plans/dto.ts`), which mirrors
 * the generated `packages/shared/src/openapi/api-types.ts` 1:1 — the generated file is not
 * reachable from the bundle (`@cb-manager/shared` exports only its barrel), so the mirror
 * lives here, as `quotas.ts` does for #12–#15.
 *
 * Why the grid reads this and not #12: the per-programme quota surface is the DEPRECATED
 * legacy in v1.6 — the plan of a place is two numbers, offline and online, with no
 * programme attached, and migration `0010_venue_plans` is the storage behind the read.
 */

import { useQuery } from '@tanstack/react-query';

import { requestEnvelope } from './client';

/** The venue's two plan numbers — the whole point of the model (T.V1). */
export interface VenuePlanDto {
  venueId: number;
  /** Master name — the byte-stable match key across sheets. */
  venueName: string;
  /** «План офлайн» — `venue_plans.plan_offline`, 0 when the venue has no row yet. */
  planOffline: number;
  /** «План онлайн» — `venue_plans.plan_online`, 0 when the venue has no row yet. */
  planOnline: number;
  /** `planOffline + planOnline` — derived by the api, never stored. */
  total: number;
  /**
   * The row's freshness marker (ISO-8601, `''` when the venue has no plan row): what the
   * seed's ledger guard compares, and the evidence an admin-written plan carries.
   */
  updatedAt: string;
}

/** `{ data, meta }` — the endpoint is not paginated, `meta` reports the row count. */
export interface VenuePlanListEnvelope {
  data: VenuePlanDto[];
  meta: { page: number; pageSize: number; total: number };
}

export const VENUE_PLANS_PATH = '/venue-plans';

export async function fetchVenuePlans(signal?: AbortSignal): Promise<VenuePlanListEnvelope> {
  const envelope = await requestEnvelope<VenuePlanDto[]>('GET', VENUE_PLANS_PATH, ['data'], { signal });
  // The endpoint is not paginated; `meta` is the row count, and a stub without one
  // falls back to the length of the rows it did send.
  return {
    data: envelope.data ?? [],
    meta: {
      page: envelope.meta?.page ?? 1,
      pageSize: envelope.meta?.pageSize ?? envelope.meta?.limit ?? envelope.data?.length ?? 0,
      total: envelope.meta?.total ?? envelope.data?.length ?? 0,
    },
  };
}

/** The cache root — the venue patch writes and the grid read share it. */
export const VENUE_PLANS_ROOT = ['venue-plans'] as const;

/** One entry (the read takes no filter — the grid filters client-side). */
export function venuePlansQueryKey(): readonly [string] {
  return [VENUE_PLANS_ROOT[0]];
}

/** 12.5 — one row per venue, the whole roster at once (189 today). */
export function useVenuePlans() {
  return useQuery({
    queryKey: venuePlansQueryKey(),
    queryFn: ({ signal }) => fetchVenuePlans(signal),
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    retry: 0,
  });
}
